import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, Plus, Download } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loading, StatusBadge } from "@/components/common";
import { FormDialog } from "@/components/FormDialog";
import { TenancyDialog, WelcomeResultDialog, CertDialog, UploadDialog, MaintDialog } from "@/components/dialogs";
import { propertyFields } from "@/pages/biz/Properties";
import { api, useFetch, errMsg, money, fmtDate, openFile, CERT_LABELS } from "@/lib/api";
import { useAuth, useCurrency } from "@/context/AuthContext";

const UNIT_FIELDS = [
  { name: "name", label: "Unit / room name", required: true, full: true },
  { name: "bedrooms", label: "Bedrooms", type: "number", step: "1", default: 1 },
  { name: "bathrooms", label: "Bathrooms", type: "number", step: "1", default: 1 },
  { name: "rent_amount", label: "Advertised rent", type: "number" },
];

function Simple({ cols, rows, empty }) {
  if (!rows.length) return <p className="text-sm text-slate-500 py-6">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow>{cols.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{rows}</TableBody>
      </Table>
    </div>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const cur = useCurrency();
  const { session } = useAuth();
  const isAgent = session.business.type === "agent";
  const { data: p, reload } = useFetch(`/biz/properties/${id}`);
  const { data: landlords } = useFetch(isAgent ? "/biz/landlords" : null);
  const [dlg, setDlg] = useState(null);
  const [welcome, setWelcome] = useState(null);
  if (!p) return <Loading />;
  const props = [p];
  const del = async () => {
    if (!window.confirm("Delete this property and its units, certificates and repairs?")) return;
    try { await api.delete(`/biz/properties/${id}`); toast.success("Property deleted"); nav("/app/properties"); } catch (e) { toast.error(errMsg(e)); }
  };
  const delUnit = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/biz/units/${u.id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const unitName = (uid) => p.units.find((u) => u.id === uid)?.name || "Whole property";
  return (
    <div data-testid="property-detail-page">
      <Link to="/app/properties" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-properties"><ArrowLeft className="w-4 h-4" />Properties</Link>
      <div className="panel p-0 overflow-hidden mb-6 grid md:grid-cols-3">
        {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-48 md:h-full object-cover" />}
        <div className={`p-6 ${p.image_url ? "md:col-span-2" : "md:col-span-3"}`}>
          <div className="eyebrow mb-2">{p.property_type}</div>
          <h1 className="text-3xl font-extrabold" data-testid="property-name">{p.name}</h1>
          <p className="text-slate-500 mt-1">{[p.address_line, p.city, p.postcode, p.country].filter(Boolean).join(", ")}</p>
          {p.landlord && <p className="text-sm mt-2">Landlord: <b>{p.landlord.name}</b></p>}
          <div className="flex flex-wrap gap-2 mt-5">
            <Button size="sm" variant="outline" onClick={() => setDlg("edit")} data-testid="edit-property-button"><Pencil className="w-4 h-4 mr-1" />Edit</Button>
            <Button size="sm" variant="outline" onClick={del} data-testid="delete-property-button"><Trash2 className="w-4 h-4 mr-1 text-rose-600" />Delete</Button>
          </div>
        </div>
      </div>
      <Tabs defaultValue="tenancies">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="tenancies" data-testid="tab-tenancies">Tenancies</TabsTrigger>
          <TabsTrigger value="units" data-testid="tab-units">Units</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">Maintenance</TabsTrigger>
        </TabsList>
        <TabsContent value="tenancies" className="panel mt-4">
          <div className="flex justify-end mb-2"><Button size="sm" onClick={() => setDlg("tenancy")} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="property-add-tenancy"><Plus className="w-4 h-4 mr-1" />New tenancy</Button></div>
          <Simple cols={["Renter", "Unit", "Period", "Rent", "Status"]} empty="No tenancies yet." rows={p.tenancies.map((t) => (
            <TableRow key={t.id}><TableCell className="font-semibold">{t.renter_name}<div className="text-xs text-slate-500 font-normal">{t.renter_email}</div></TableCell>
              <TableCell>{unitName(t.unit_id)}</TableCell><TableCell className="text-sm">{fmtDate(t.start_date)} – {fmtDate(t.end_date)}</TableCell>
              <TableCell>{money(t.rent_amount, cur)} / {t.rent_frequency}</TableCell><TableCell><StatusBadge status={t.status} /></TableCell></TableRow>
          ))} />
        </TabsContent>
        <TabsContent value="units" className="panel mt-4">
          <div className="flex justify-end mb-2"><Button size="sm" onClick={() => setDlg({ unit: {} })} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-unit-button"><Plus className="w-4 h-4 mr-1" />Add unit</Button></div>
          <Simple cols={["Unit", "Beds", "Baths", "Rent", ""]} empty="No units — the whole property is let as one." rows={p.units.map((u) => (
            <TableRow key={u.id} data-testid={`unit-row-${u.id}`}><TableCell className="font-semibold">{u.name}</TableCell><TableCell>{u.bedrooms}</TableCell><TableCell>{u.bathrooms}</TableCell>
              <TableCell>{money(u.rent_amount, cur)}</TableCell>
              <TableCell className="text-right whitespace-nowrap"><Button variant="ghost" size="icon" onClick={() => setDlg({ unit: u })}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => delUnit(u)}><Trash2 className="w-4 h-4 text-rose-600" /></Button></TableCell></TableRow>
          ))} />
        </TabsContent>
        <TabsContent value="compliance" className="panel mt-4">
          <div className="flex justify-end mb-2"><Button size="sm" onClick={() => setDlg("cert")} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="property-add-cert"><Plus className="w-4 h-4 mr-1" />Add certificate</Button></div>
          <Simple cols={["Certificate", "Reference", "Expires", "Status"]} empty="No certificates recorded." rows={p.compliance.map((c) => (
            <TableRow key={c.id}><TableCell className="font-semibold">{CERT_LABELS[c.cert_type]}</TableCell><TableCell>{c.reference}</TableCell>
              <TableCell>{fmtDate(c.expiry_date)}</TableCell><TableCell><StatusBadge status={c.status} /></TableCell></TableRow>
          ))} />
        </TabsContent>
        <TabsContent value="documents" className="panel mt-4">
          <div className="flex justify-end mb-2"><Button size="sm" onClick={() => setDlg("upload")} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="property-upload-doc"><Plus className="w-4 h-4 mr-1" />Upload</Button></div>
          <Simple cols={["Name", "Category", "Renter can see", ""]} empty="No documents." rows={p.documents.map((d) => (
            <TableRow key={d.id}><TableCell className="font-semibold">{d.name}</TableCell><TableCell className="capitalize">{d.category}</TableCell>
              <TableCell>{d.shared_with_renter ? "Yes" : "No"}</TableCell>
              <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => openFile(`/biz/documents/${d.id}/download`).catch((e) => toast.error(errMsg(e)))}><Download className="w-4 h-4" /></Button></TableCell></TableRow>
          ))} />
        </TabsContent>
        <TabsContent value="maintenance" className="panel mt-4">
          <div className="flex justify-end mb-2"><Button size="sm" onClick={() => setDlg("maint")} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="property-add-maint"><Plus className="w-4 h-4 mr-1" />Log request</Button></div>
          <Simple cols={["Issue", "Priority", "Reported", "Status"]} empty="No maintenance requests." rows={p.maintenance.map((m) => (
            <TableRow key={m.id}><TableCell className="font-semibold">{m.title}</TableCell><TableCell><StatusBadge status={m.priority} /></TableCell>
              <TableCell className="text-sm">{fmtDate(m.created_at)} · {m.reported_by}</TableCell><TableCell><StatusBadge status={m.status} /></TableCell></TableRow>
          ))} />
        </TabsContent>
      </Tabs>

      <FormDialog open={dlg === "edit"} onOpenChange={(o) => !o && setDlg(null)} title="Edit property" testid="property-edit-form"
        fields={propertyFields(landlords, isAgent)} initial={p}
        onSubmit={async (v) => { await api.put(`/biz/properties/${id}`, v); toast.success("Saved"); reload(); }} />
      <FormDialog open={!!dlg?.unit} onOpenChange={(o) => !o && setDlg(null)} title={dlg?.unit?.id ? "Edit unit" : "Add unit"} testid="unit-form"
        fields={UNIT_FIELDS} initial={dlg?.unit}
        onSubmit={async (v) => {
          if (dlg.unit.id) await api.put(`/biz/units/${dlg.unit.id}`, v); else await api.post(`/biz/properties/${id}/units`, v);
          toast.success("Unit saved"); reload();
        }} />
      <TenancyDialog open={dlg === "tenancy"} onOpenChange={(o) => !o && setDlg(null)} properties={props} units={p.units} presetProperty={id}
        onCreated={(r) => { reload(); setWelcome(r.welcome); }} />
      <WelcomeResultDialog welcome={welcome} onClose={() => setWelcome(null)} />
      <CertDialog open={dlg === "cert"} onOpenChange={(o) => !o && setDlg(null)} properties={props} presetProperty={id} onSaved={reload} />
      <UploadDialog open={dlg === "upload"} onOpenChange={(o) => !o && setDlg(null)} properties={props} tenancies={p.tenancies} presetProperty={id} onSaved={reload} />
      <MaintDialog open={dlg === "maint"} onOpenChange={(o) => !o && setDlg(null)} properties={props} presetProperty={id} onSaved={reload} />
    </div>
  );
}
